/**
 * Generation state management — observable progress + block diff state.
 *
 * Extracted from register-generation-handlers.ts so UI components can
 * subscribe to generation progress without depending on handler registration.
 * Handler registration is now in register-studio-tools.ts (MCP direct).
 */

import { useStudioStore } from '@/store/studio-store'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import type { LoadedApp } from '@/features/page-editor/store/types'
import { blockApi, toV2Bundle, type BlockSpec, type AppSpecPartial, type AppSpecFull, type BlockBundle } from '@/services/block-api-service'

// ─── Tier-Aware Block Filtering ────────────────────────────────────

/** Block types that are forbidden for Tier B (Simple App) */
export const TIER_B_FORBIDDEN_BLOCK_TYPES = new Set([
  'crud', 'admin', 'data-table', 'auth', 'login', 'signup',
  'dashboard', 'settings', 'user-management',
])

/** Block description patterns that indicate Tier A features */
const TIER_B_FORBIDDEN_PATTERNS = [
  /\b(crud|auth|login|signup|register)\b/i,
  /\b(database|supabase|api\s+call|rest\s*api)\b/i,
  /\b(create|update|delete)\s+(record|data|entry|item|user)/i,
]

/**
 * Filter a block spec for tier compatibility.
 * Returns null if the block type/description is forbidden for the given tier.
 * Returns the blockSpec unchanged for Tier A or when no tier is set.
 */
export function filterBlockSpecForTier(
  tier: 'A' | 'B' | null | undefined,
  blockSpec: BlockSpec,
): BlockSpec | null {
  if (tier !== 'B') return blockSpec

  // Check block type
  if (TIER_B_FORBIDDEN_BLOCK_TYPES.has(blockSpec.type.toLowerCase())) {
    return null
  }

  // Check description for Tier A patterns
  const desc = blockSpec.description ?? ''
  if (TIER_B_FORBIDDEN_PATTERNS.some(p => p.test(desc))) {
    return null
  }

  return blockSpec
}

// ─── Block Diff State (module-level for BlockDiffCard access) ──────

export interface BlockDiffData {
  blockName: string
  beforeSummary: string
  afterSummary: string
  changedFields: string[]
  applied: boolean
  /** Internal: the original block snapshot for undo */
  _beforeBlock: Record<string, unknown>
}

let _blockDiff: BlockDiffData | null = null
const _diffListeners = new Set<(diff: BlockDiffData | null) => void>()

export function getBlockDiff(): BlockDiffData | null {
  return _blockDiff
}

export function onBlockDiff(listener: (diff: BlockDiffData | null) => void): () => void {
  _diffListeners.add(listener)
  return () => _diffListeners.delete(listener)
}

export function setBlockDiff(diff: BlockDiffData | null) {
  _blockDiff = diff
  for (const l of _diffListeners) l(diff)
}

/** Extract a one-line summary of a block's key properties. */
export function summarizeBlock(block: Record<string, unknown>): string {
  const meta = block.meta as Record<string, unknown> | undefined
  const ui = block.ui as Record<string, unknown> | undefined
  const root = ui?.root as Record<string, unknown> | undefined

  const parts: string[] = []
  if (meta?.blockType) parts.push(`type=${meta.blockType}`)
  else if (meta?.type) parts.push(`type=${meta.type}`)

  // Extract top-level className or variant
  if (root?.className) parts.push(`class="${String(root.className).slice(0, 40)}"`)
  if (root?.variant) parts.push(`variant=${root.variant}`)

  // Count children
  const children = root?.children as unknown[] | undefined
  if (Array.isArray(children)) parts.push(`${children.length} children`)

  // Count flows
  const flow = block.flow as Record<string, unknown> | undefined
  const flows = flow?.flows as Record<string, unknown> | typeof flow
  if (flows && typeof flows === 'object') {
    const flowCount = Object.keys(flows).filter((k) => k !== 'flows').length
    if (flowCount > 0) parts.push(`${flowCount} flows`)
  }

  return parts.join(', ') || 'block'
}

/** Shallow diff of two block objects, returning changed key paths. */
export function findChangedFields(before: Record<string, unknown>, after: Record<string, unknown>): string[] {
  const changed: string[] = []
  const FILE_KEYS = ['meta', 'ui', 'events', 'flow', 'state', 'data']

  for (const key of FILE_KEYS) {
    const a = JSON.stringify(before[key] ?? {})
    const b = JSON.stringify(after[key] ?? {})
    if (a !== b) changed.push(key)
  }

  return changed
}

// ─── Generation Progress State (module-level for chat UI access) ──

export interface GenerationProgress {
  stage: 'generating' | 'validating' | 'fixing' | 'complete' | 'error'
  blockName: string
  progress: number // 0-100
  score?: number
  grade?: string
  error?: string
  issues?: Array<{ stage: number; level: string; message: string }>
  fixed?: boolean
}

let _generationProgress: GenerationProgress | null = null
const _listeners = new Set<(progress: GenerationProgress | null) => void>()

export function getGenerationProgress(): GenerationProgress | null {
  return _generationProgress
}

export function onGenerationProgress(listener: (progress: GenerationProgress | null) => void): () => void {
  _listeners.add(listener)
  return () => _listeners.delete(listener)
}

export function setProgress(progress: GenerationProgress | null) {
  _generationProgress = progress
  for (const listener of _listeners) {
    listener(progress)
  }
}

// ─── Generation Log (chat-visible progress messages) ──────────────

export interface GenerationLogEntry {
  timestamp: number
  type: 'info' | 'success' | 'error' | 'progress'
  message: string
  blockName?: string
  score?: number
}

let _generationLog: GenerationLogEntry[] = []
const _logListeners = new Set<(log: GenerationLogEntry[]) => void>()

export function addGenerationLog(entry: Omit<GenerationLogEntry, 'timestamp'>) {
  _generationLog = [..._generationLog, { ...entry, timestamp: Date.now() }]
  for (const listener of _logListeners) listener(_generationLog)
}

export function clearGenerationLog() {
  _generationLog = []
  for (const listener of _logListeners) listener(_generationLog)
}

export function getGenerationLog(): GenerationLogEntry[] {
  return _generationLog
}

export function onGenerationLog(listener: (log: GenerationLogEntry[]) => void): () => void {
  _logListeners.add(listener)
  return () => _logListeners.delete(listener)
}

// ─── Validation summary (post-generation) ─────────────────────

export interface ValidationSummary {
  blocks: Array<{
    name: string
    score: number
    grade: string
    issues: Array<{ stage?: number; level: string; message: string; rule?: string; path?: string }>
    enrichedFeedback?: string
  }>
  averageScore: number
  overallGrade: string
  generatedAt?: number
}

let validationSummary: ValidationSummary | null = null
const validationSubscribers = new Set<(summary: ValidationSummary | null) => void>()

export function setValidationSummary(summary: ValidationSummary): void {
  validationSummary = { ...summary, generatedAt: summary.generatedAt ?? Date.now() }
  for (const cb of validationSubscribers) cb(validationSummary)
}

export function getValidationSummary(): ValidationSummary | null {
  return validationSummary
}

export function clearValidationSummary(): void {
  validationSummary = null
  for (const cb of validationSubscribers) cb(null)
}

export function onValidationSummary(
  cb: (summary: ValidationSummary | null) => void,
): () => void {
  validationSubscribers.add(cb)
  return () => { validationSubscribers.delete(cb) }
}

// ─── Shared Helper: Insert block into project ──────────────────

export function insertBlockIntoProject(blockName: string, bundle: BlockBundle) {
  const v2 = toV2Bundle(bundle, blockName)

  useStudioStore.setState((s) => {
    if (!s.currentProject) return

    // Insert v2 BlockDef into project blocks
    s.currentProject.blocks[blockName] = v2 as any

    // Merge flows from v2.flow into project flows
    if (v2.flow && typeof v2.flow === 'object') {
      const flows = (v2.flow as any).flows ?? v2.flow
      s.currentProject.flows = {
        ...s.currentProject.flows,
        ...flows,
      } as any
    }
  })

  // Also update editor store if in page mode
  const editorApp = useEditorStore.getState().app
  if (editorApp) {
    const project = useStudioStore.getState().currentProject
    if (project) {
      useEditorStore.getState().loadApp(project as LoadedApp)
    }
  }
}
